import { emptySplitApi } from './injectApi';
import { ICCID_ORGANIZATION_PATH } from '../../../constants';

const iccidApi = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    getIccid: build.query({
      query: (params) => ({
        url: '/sales-admin/sales/admin/iccids',
        method: 'GET',
        params,
        headers: {
          Referrer: 2,
        },
      }),
      providesTags: ['Iccid'],
    }),
    getIccidLogs: build.query({
      query: (params) => ({
        url: '/sales-admin/sales/admin/iccids/log',
        method: 'GET',
        params,
      }),
    }),
    getIccidLogsDownload: build.query({
      query: (params) => ({
        url: '/sales-admin/sales/admin/iccids/log/download',
        method: 'GET',
        params,
        responseType: 'blob',
      }),
    }),
    putIccid: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/${data.id}/partners/${data.partnerId}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Iccid'],
    }),
    postIccidHub: build.mutation({
      query: ({ minId, maxId, hubId }) => ({
        url: `/sales-admin/sales/admin/iccids/minId/${minId}/maxId/${maxId}/hubs/${hubId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Iccid'],
    }),
    postIccid: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/partners/${data.partnerId}`,
        method: 'POST',
        data: {
          identifiers: data.identifiers,
        },
      }),
      invalidatesTags: ['Iccid'],
    }),
    postAssignIccidWithFile: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/assign/xls/file`,
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),
    postUnassignIccidWithFile: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/unassign/xls/file`,
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),
    postAssignIccidWithFileTransfer: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/transfer/report/file`,
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),
    postAssignIccidWithList: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/partners/${data.partnerId}/list`,
        method: 'POST',
        data: data.body,
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),
    postUnassignIccidWithList: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/unassign/identifiers/list`,
        method: 'POST',
        data,
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),
    postAssignIccidWithListTransfer: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/partners/list/transfer`,
        method: 'POST',
        data,
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),
    // door: 'range' | 'bulk' | 'file'; action: 'assign' | 'unassign' | 'transfer'.
    // All nine combinations answer 200 with a { summary, items } report.
    postIccidHolderChange: build.mutation({
      query: ({ door, action, data }) => ({
        url: `${ICCID_ORGANIZATION_PATH}/${door}/${action}`,
        method: 'POST',
        data,
        headers: {
          'X-Correlation-Id': window.crypto?.randomUUID?.(),
          ...(door === 'file' && { 'Content-Type': 'multipart/form-data' }),
        },
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: ['Iccid'],
    }),
    postAssignIccidHubWithFile: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/hubs/${data.hubID}/file`,
        method: 'POST',
        data: data.form,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
      invalidatesTags: ['Stock'],
    }),
    postAssignIccidHubWithList: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/hubs/${data.hubID}/list`,
        method: 'POST',
        data: data.body,
      }),
      invalidatesTags: ['Stock'],
    }),
    postIccidWithRange: build.mutation({
      query: ({ minId, maxId, partnerId }) => ({
        url: `/sales-admin/sales/admin/iccids/minId/${minId}/maxId/${maxId}/partners/${partnerId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),
    postIccidWithRangeUnassign: build.mutation({
      query: ({ minId, maxId }) => ({
        url: `/sales-admin/sales/admin/iccids/unassign/minId/${minId}/maxId/${maxId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),

    postIccidWithRangeTransfer: build.mutation({
      query: (data) => ({
        url: `/sales-admin/sales/admin/iccids/partners/range/transfer`,
        method: 'POST',
        data,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        responseType: 'blob',
      }),
      invalidatesTags: ['Iccid'],
    }),
  }),
});

export const iccidApiHooks = {
  useGetIccid: iccidApi.useGetIccidQuery,
  // useGetIccidLogs: iccidApi.useGetIccidLogsQuery,
  useGetIccidLogs: iccidApi.useLazyGetIccidLogsQuery,
  useGetIccidLogsDownload: iccidApi.useLazyGetIccidLogsDownloadQuery,
  usePutIccid: iccidApi.usePutIccidMutation,
  usePostIccid: iccidApi.usePostIccidMutation,
  usePostIccidWithRange: iccidApi.usePostIccidWithRangeMutation,
  usePostIccidWithRangeUnassign: iccidApi.usePostIccidWithRangeUnassignMutation,
  usePostIccidHub: iccidApi.usePostIccidHubMutation,
  usePostUnassignIccidWithList: iccidApi.usePostUnassignIccidWithListMutation,
  usePostAssignIccidWithFile: iccidApi.usePostAssignIccidWithFileMutation,
  usePostUnassignIccidWithFile: iccidApi.usePostUnassignIccidWithFileMutation,
  usePostAssignIccidWithList: iccidApi.usePostAssignIccidWithListMutation,
  usePostAssignIccidHubWithFile: iccidApi.usePostAssignIccidHubWithFileMutation,
  usePostAssignIccidHubWithList: iccidApi.usePostAssignIccidHubWithListMutation,
  usePostAssignIccidWithFileTransfer: iccidApi.usePostAssignIccidWithFileTransferMutation,
  usePostAssignIccidWithListTransfer: iccidApi.usePostAssignIccidWithListTransferMutation,
  usePostIccidWithRangeTransfer: iccidApi.usePostIccidWithRangeTransferMutation,
  usePostIccidHolderChange: iccidApi.usePostIccidHolderChangeMutation,
};
